import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  FileText, 
  ArrowRight,
  Stethoscope,
  Building2,
  Shield,
  LogIn,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  CheckCircle,
  Users,
  ClipboardList,
  Gavel,
  BarChart3,
  Zap
} from "lucide-react";

const ADMIN_TYPES = [
  {
    type: "provider",
    name: "Provider Admin",
    icon: Stethoscope,
    color: "emerald",
    email: "provider@demo.preauthpro.com",
    password: "demo123",
    description: "Hospital / Clinic administrators managing PA submissions",
    features: [
      "Submit & track PA requests",
      "Patient management",
      "AI-powered ICD/CPT coding",
      "Eligibility verification",
      "Denial management & appeals"
    ]
  },
  {
    type: "tpa",
    name: "TPA Admin",
    icon: Building2,
    color: "blue",
    email: "tpa@demo.preauthpro.com",
    password: "demo123",
    description: "Third Party Administrator reviewing PA requests from providers",
    features: [
      "Centralized review queue",
      "Case assignment & tracking",
      "Multi-provider management",
      "SLA monitoring",
      "278 EDI support"
    ]
  },
  {
    type: "payer",
    name: "Payor Admin",
    icon: Shield,
    color: "purple",
    email: "payer@demo.preauthpro.com",
    password: "demo123",
    description: "Insurance company administrators with final PA decision authority",
    features: [
      "Final approval/denial authority",
      "Medical necessity review",
      "Policy compliance checking",
      "Appeals management",
      "278 EDI response generation"
    ]
  }
];

const colorMap = {
  emerald: {
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    text: "text-emerald-400",
    icon: "bg-emerald-500/20",
    button: "bg-emerald-600 hover:bg-emerald-500",
    ring: "ring-emerald-500/40",
    glow: "shadow-emerald-500/20"
  },
  blue: {
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    text: "text-blue-400",
    icon: "bg-blue-500/20",
    button: "bg-blue-600 hover:bg-blue-500",
    ring: "ring-blue-500/40",
    glow: "shadow-blue-500/20"
  },
  purple: {
    bg: "bg-purple-500/10",
    border: "border-purple-500/30",
    text: "text-purple-400",
    icon: "bg-purple-500/20",
    button: "bg-purple-600 hover:bg-purple-500",
    ring: "ring-purple-500/40",
    glow: "shadow-purple-500/20"
  }
};

export default function LandingPage() {
  const { loginWithPassword, register, user } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [registerName, setRegisterName] = useState("");
  const [registerOrgName, setRegisterOrgName] = useState("");

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactSubmitting, setContactSubmitting] = useState(false);

  const handleDemoLogin = (admin) => {
    setSelectedType(admin.type);
    setLoginEmail("");
    setLoginPassword("");
    setRegisterName("");
    setRegisterOrgName("");
    setLoginError("");
    setAuthTab("login");
    setShowLoginModal(true);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactFirstName.trim() || !contactLastName.trim() || !contactEmail.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setContactSubmitting(true);
    try {
      await axios.post(`${API}/contact-us`, {
        first_name: contactFirstName,
        last_name: contactLastName,
        email: contactEmail
      }, { withCredentials: true });
      toast.success("Thank you! Your request has been sent to contactus@priorath.com.");
      setShowContactModal(false);
      setContactFirstName("");
      setContactLastName("");
      setContactEmail("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit contact request. Please try again.");
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    const result = await loginWithPassword(loginEmail, loginPassword);
    setLoginLoading(false);
    if (result.success) {
      setShowLoginModal(false);
      navigate("/pa-requests");
    } else {
      setLoginError(result.error);
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    const orgType = selectedType || "provider";
    const result = await register(loginEmail, loginPassword, registerName, orgType, registerOrgName);
    setLoginLoading(false);
    if (result.success) {
      setShowLoginModal(false);
      navigate("/pa-requests");
    } else {
      setLoginError(result.error);
    }
  };

  const openLoginModal = (type) => {
    setSelectedType(type);
    setLoginEmail("");
    setLoginPassword("");
    setRegisterName("");
    setRegisterOrgName("");
    setLoginError("");
    setAuthTab("login");
    setShowLoginModal(true);
  };

  useEffect(() => {
    if (user) {
      navigate("/pa-requests");
    }
  }, [user, navigate]);

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800/50 bg-[#0d1321]/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-900" />
              </div>
              <div>
                <span className="font-bold text-lg text-white tracking-tight">Priorath</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-5xl w-full">
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-8">
              Select Your Admin Type
            </h1>
          </div>

          {/* Admin Type Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {ADMIN_TYPES.map((admin) => {
              const colors = colorMap[admin.color];
              const Icon = admin.icon;
              return (
                <Card 
                  key={admin.type}
                  className={`bg-slate-900/60 ${colors.border} hover:shadow-xl hover:${colors.glow} transition-all duration-300 group cursor-pointer`}
                  data-testid={`admin-card-${admin.type}`}
                  onClick={() => handleDemoLogin(admin)}
                >
                  <CardContent className="p-6">
                    {/* Icon & Title */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl ${colors.icon} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className={`w-7 h-7 ${colors.text}`} />
                      </div>
                      <div>
                        <h3 className={`text-xl font-bold ${colors.text}`}>{admin.name}</h3>
                      </div>
                    </div>

                    <p className="text-sm text-slate-400 mb-4 leading-relaxed">{admin.description}</p>

                    {/* Features */}
                    <ul className="space-y-2 mb-5">
                      {admin.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-slate-500">
                          <CheckCircle className={`w-3.5 h-3.5 ${colors.text} flex-shrink-0`} />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* Login Button */}
                    <Button 
                      className={`w-full ${colors.button} text-white font-semibold`}
                      data-testid={`demo-login-${admin.type}`}
                      disabled={loginLoading}
                    >
                      {loginLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      Enter as {admin.name}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-xs text-slate-600">
            Priorath Sandbox Environment &mdash; sandbox.priorath.com &mdash; Dev Branch
          </p>
        </div>
      </footer>

      {/* Login/Register Dialog */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-400" />
              Priorath - {authTab === "login" ? "Sign In" : "Contact Us"}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={authTab} onValueChange={setAuthTab} className="w-full">
            <TabsList className="w-full bg-slate-800 border border-slate-700">
              <TabsTrigger value="login" className="flex-1 data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
                Sign In
              </TabsTrigger>
              <TabsTrigger value="register" className="flex-1 data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
                Contact Us
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Enter password"
                      className="bg-slate-800 border-slate-700 text-white pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {loginError && (
                  <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="login-error">
                    <AlertCircle className="w-4 h-4" />
                    {loginError}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                  disabled={loginLoading}
                  data-testid="login-submit-btn"
                >
                  {loginLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
                  Sign In
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-4">
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="contact-firstname" className="text-slate-300">First Name</Label>
                  <Input
                    id="contact-firstname"
                    type="text"
                    required
                    value={contactFirstName}
                    onChange={(e) => setContactFirstName(e.target.value)}
                    placeholder="John"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-lastname" className="text-slate-300">Last Name</Label>
                  <Input
                    id="contact-lastname"
                    type="text"
                    required
                    value={contactLastName}
                    onChange={(e) => setContactLastName(e.target.value)}
                    placeholder="Doe"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email" className="text-slate-300">Email Address</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    required
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="john.doe@example.com"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold mt-4"
                  disabled={contactSubmitting}
                >
                  {contactSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                  Submit Contact Request
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
